

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AccessControlAPI, AccessSystemsAPI } from "../../api/accessControl";

export default function AccessControlUsersPage() {
  const navigate = useNavigate();

  const [systems, setSystems] = useState([]);
  const [users, setUsers] = useState([]);
  const [systemId, setSystemId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadSystems() {
    const data = await AccessSystemsAPI.list();
    const enabledSystems = Array.isArray(data)
      ? data.filter((system) => system.enabled)
      : [];

    setSystems(enabledSystems);
  }

  async function loadUsers() {
    try {
      setLoading(true);
      setError("");

      const data = await AccessControlAPI.listUsers({
        systemId: systemId || null,
        schoolId: schoolId || null,
        search,
      });

      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load access control users", err);
      setError("Failed to load access control users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSystems().catch((err) => {
      console.error("Failed to load access systems", err);
      setError("Failed to load access systems");
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadUsers();
  }, [systemId, schoolId]);

  function handleSystemChange(value) {
    const selected = systems.find((system) => String(system.id) === value);

    setSystemId(value);
    setSchoolId(selected ? selected.school_id : "");
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    loadUsers();
  }

  function getUserDisplayName(user) {
    const parts = [user.first_name, user.surname].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(" ");
    }

    return user.email || `Net2 user ${user.net2_user_id}`;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Net2 Users</h1>
        <p className="text-sm text-gray-600">
          View synced Net2 users from each access control system.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="rounded border bg-white p-4 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium mb-1">System</label>
            <select
              value={systemId}
              onChange={(e) => handleSystemChange(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">All systems</option>
              {systems.map((system) => (
                <option key={system.id} value={system.id}>
                  {system.school_name} — {system.system_name}
                </option>
              ))}
            </select>
          </div>

          <form onSubmit={handleSearchSubmit} className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Search</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded border p-2"
                placeholder="Search by name or email"
              />
              <button
                type="submit"
                className="rounded bg-blue-600 px-4 py-2 text-white"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="rounded border bg-white">
        <div className="flex items-center justify-between border-b p-3">
          <h2 className="font-medium">Users</h2>
          <button
            type="button"
            onClick={loadUsers}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No Net2 users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">School</th>
                  <th className="p-3">System</th>
                  <th className="p-3">Net2 ID</th>
                  <th className="p-3">Last Synced</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="cursor-pointer border-b hover:bg-gray-50"
                    onClick={() => navigate(`/admin/access-control/users/${user.id}`)}
                  >
                    <td className="p-3 font-medium">{getUserDisplayName(user)}</td>
                    <td className="p-3">{user.email || "-"}</td>
                    <td className="p-3">{user.school_name || user.school_id || "-"}</td>
                    <td className="p-3">{user.system_name || user.system_id || "-"}</td>
                    <td className="p-3">{user.net2_user_id}</td>
                    <td className="p-3">
                      {user.last_synced_at
                        ? new Date(user.last_synced_at).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}